import { msg } from '@lingui/core/macro';

import { filterAction } from '@/shared/filters/filter-action';
import { listPageAction } from '@/shared/patterns/list-search';
import { action, can, defineScreen } from '@/shared/screen/define';

import { automaticPixHistoryFilters } from './automatic-pix.search';
import { BANK_ACCOUNT_LIST_PATH } from './bank-account.search';
import { preparedAction } from './beneficiary-limits.screen';

// Telas do Pix Automático (U6, U23/U24): o painel e as cinco listas, irmãs do detalhe (`router.tsx:520-563`). Leve:
// o fio entra pela tela.
//
// O agente **só prepara**: "Ler QR Code para autorização" abre o passo do QR em branco (nenhum código passa pela
// ferramenta); aprovar, reprovar, cancelar a autorização e cancelar o agendamento abrem o pedido de senha e código MFA
// da autorização ou do agendamento **aberto no painel de detalhes**, e só existem enquanto ele está aberto. Entrada
// sempre vazia (`strictObject`), presa à conta da URL e à seleção da tela.
//
// Acesso como o legado: `BankAccount:Read` **ou** `ViewTransitoryAccount` (`router.tsx:375-381`) e a flag
// `AUTOMATIC_PIX === 'Enable'`. Nenhuma ação confere permissão no front (fio §6, registrado).

// eslint-disable-next-line lingui/no-unlocalized-strings -- chave e valor da flag do config
const ACCESS = { anyOf: [can('BankAccount', 'Read'), can('BankAccount', 'ViewTransitoryAccount')], flag: { key: 'FEATURE_FLAGS.AUTOMATIC_PIX', equals: 'Enable' } };
const BASE = `${BANK_ACCOUNT_LIST_PATH}/$bankAccountId/pix/pix-automatico`;

const refresh = (id: string, description: ReturnType<typeof msg>) => action({ id, kind: 'read', label: msg`Atualizar`, description, run: () => undefined });

// ─── Painel ───────────────────────────────────────────────────────────────────────────────────────

/** "Ler QR Code para autorização" (`AutomaticPix.tsx:18-23`): QR → conferência → senha e código MFA. */
export const automaticPixAuthorize = preparedAction(
  'bankAccount.automaticPix.authorize',
  msg`Ler QR Code para autorização`,
  'write',
  msg`Abre "Pix automático" da conta aberta no passo do QR Code, em branco. Quem cola o código, confere o recebedor, a recorrência e o valor máximo e confirma com a senha e o código MFA é a pessoa: esta ferramenta não lê código nem autoriza nada. Não recebe entrada.`,
  msg`Autorização do Pix automático`,
);

export const bankAccountAutomaticPixScreen = defineScreen({
  id: 'bankAccount.automaticPix',
  path: BASE,
  title: msg`Pix Automático`,
  description: msg`O painel do Pix Automático de uma conta digital: ler um QR Code com recorrência para autorizar os pagamentos (com valor máximo opcional, senha e código MFA) e os atalhos para as autorizações pendentes, as ativas, os pagamentos agendados e o histórico de autorizações.`,
  access: ACCESS,
  // Irmã do detalhe na seção "Contas" (`router.tsx:520`), como a transitória: com `parent` no detalhe, a trilha pediria
  // `BankAccount:Read` e barraria quem só tem `ViewTransitoryAccount`.
  place: { icon: 'wallet', section: 'bank-account', record: msg`Pix Automático` },
  actions: [automaticPixAuthorize],
});

// ─── Histórico ────────────────────────────────────────────────────────────────────────────────────

export const automaticPixHistoryRefresh = refresh('bankAccount.automaticPix.history.refresh', msg`Busca de novo o histórico de autorizações do Pix Automático da conta aberta. Nada é alterado.`);

export const automaticPixHistoryFilter = filterAction('bankAccount.automaticPix.history.filter', automaticPixHistoryFilters, {
  description: msg`Filtra o histórico de autorizações do Pix Automático da conta aberta por pxa_status: Processing (Processando Sistema), Confirmed (Confirmado), Rejected (Rejeitado), Canceled (Cancelado), PendingPayer (Pendente Pagador) ou PendingUser (Pendente Usuário). Filtro omitido é limpo.`,
});

export const automaticPixHistoryPage = listPageAction('bankAccount.automaticPix.history.page');

export const bankAccountAutomaticPixHistoryScreen = defineScreen({
  id: 'bankAccount.automaticPix.history',
  path: `${BASE}/historico`,
  title: msg`Histórico de autorizações`,
  description: msg`O histórico de autorizações do Pix Automático de uma conta digital: o recebedor ou o objeto, o início da autorização, o status e o motivo, cinco por página, com o filtro por status e Atualizar. Só consulta.`,
  access: ACCESS,
  place: { icon: 'wallet', parent: 'bankAccount.automaticPix', record: msg`Histórico` },
  actions: [automaticPixHistoryRefresh, automaticPixHistoryFilter, automaticPixHistoryPage],
});

// ─── Pendentes ────────────────────────────────────────────────────────────────────────────────────

export const automaticPixPendingRefresh = refresh('bankAccount.automaticPix.pending.refresh', msg`Busca de novo as autorizações do Pix Automático que aguardam a sua aprovação na conta aberta. Nada é alterado.`);
export const automaticPixPendingPage = listPageAction('bankAccount.automaticPix.pending.page');

export const automaticPixApprove = preparedAction(
  'bankAccount.automaticPix.pending.approve',
  msg`Aprovar Pix automático`,
  'write',
  msg`Prepara a aprovação da autorização pendente aberta nos Detalhes da autorização. Só abre o pedido de senha e código MFA: esta ferramenta não aprova nada. Não recebe entrada; o alvo é a autorização aberta na tela.`,
  msg`Aprovar Pix automático`,
);

export const automaticPixReject = preparedAction(
  'bankAccount.automaticPix.pending.reject',
  msg`Reprovar Pix automático`,
  'destructive',
  msg`Prepara a reprovação da autorização pendente aberta nos Detalhes da autorização. Só abre o pedido de senha e código MFA: esta ferramenta não reprova nada. Não recebe entrada; o alvo é a autorização aberta na tela.`,
  msg`Reprovar Pix automático`,
);

export const bankAccountAutomaticPixPendingScreen = defineScreen({
  id: 'bankAccount.automaticPix.pending',
  path: `${BASE}/pendentes`,
  title: msg`Autorizações pendentes`,
  description: msg`As autorizações do Pix Automático que aguardam aprovação numa conta digital: o recebedor, o início, a validade, a recorrência e o valor, cinco por página, com Atualizar. Clicar numa abre os Detalhes da autorização, com Aprovar e Reprovar, que pedem senha e código MFA.`,
  access: ACCESS,
  place: { icon: 'wallet', parent: 'bankAccount.automaticPix', record: msg`Pendentes` },
  actions: [automaticPixPendingRefresh, automaticPixPendingPage, automaticPixApprove, automaticPixReject],
});

// ─── Ativas ───────────────────────────────────────────────────────────────────────────────────────

export const automaticPixActiveRefresh = refresh('bankAccount.automaticPix.active.refresh', msg`Busca de novo as autorizações ativas do Pix Automático da conta aberta. Nada é alterado.`);
export const automaticPixActivePage = listPageAction('bankAccount.automaticPix.active.page');

export const automaticPixCancel = preparedAction(
  'bankAccount.automaticPix.active.cancel',
  msg`Cancelar autorização`,
  'destructive',
  msg`Prepara o cancelamento da autorização ativa aberta no painel Autorização do Pix automático. Só abre o pedido de senha e código MFA: esta ferramenta não cancela nada. Não recebe entrada; o alvo é a autorização aberta na tela.`,
  msg`Cancelar Pix automático`,
);

export const bankAccountAutomaticPixActiveScreen = defineScreen({
  id: 'bankAccount.automaticPix.active',
  path: `${BASE}/ativas`,
  title: msg`Autorizações ativas`,
  description: msg`As autorizações do Pix Automático em vigor numa conta digital, pelo nome do recebedor, cinco por página, com Atualizar. Clicar numa abre os dados da autorização (recebedor, primeiro pagamento, valor, recorrência e prazo) com Cancelar autorização, que pede senha e código MFA.`,
  access: ACCESS,
  place: { icon: 'wallet', parent: 'bankAccount.automaticPix', record: msg`Ativas` },
  actions: [automaticPixActiveRefresh, automaticPixActivePage, automaticPixCancel],
});

// ─── Agendados e Realizados ───────────────────────────────────────────────────────────────────────

export const automaticPixScheduledPage = listPageAction('bankAccount.automaticPix.scheduled.page');

export const automaticPixCancelScheduled = preparedAction(
  'bankAccount.automaticPix.scheduled.cancel',
  msg`Cancelar agendamento`,
  'destructive',
  msg`Prepara o cancelamento do pagamento agendado aberto nos Detalhes do agendamento (só aquele pagamento, não a autorização). Só abre o pedido de senha e código MFA: esta ferramenta não cancela nada. Não recebe entrada; o alvo é o agendamento aberto na tela.`,
  msg`Confirmar cancelamento`,
);

export const bankAccountAutomaticPixScheduledScreen = defineScreen({
  id: 'bankAccount.automaticPix.scheduled',
  path: `${BASE}/agendados`,
  title: msg`Pagamentos agendados`,
  description: msg`Os pagamentos do Pix Automático agendados numa conta digital, agrupados por dia, com o recebedor, o status, o valor e a data, cinco por página, e a aba Realizados. Clicar num abre os Detalhes do agendamento, com Cancelar agendamento, que pede senha e código MFA.`,
  access: ACCESS,
  place: { icon: 'wallet', parent: 'bankAccount.automaticPix', record: msg`Agendados` },
  actions: [automaticPixScheduledPage, automaticPixCancelScheduled],
});

export const automaticPixPaidPage = listPageAction('bankAccount.automaticPix.paid.page');

export const automaticPixPaidCancel = preparedAction(
  'bankAccount.automaticPix.paid.cancel', msg`Cancelar autorização`, 'destructive',
  msg`Prepara a reprovação da autorização do pagamento aberto nos detalhes. A pessoa confirma com senha e código MFA; o assistente não envia a operação.`, msg`Reprovar Pix automático`,
);

export const bankAccountAutomaticPixPaidScreen = defineScreen({
  id: 'bankAccount.automaticPix.paid',
  path: `${BASE}/agendados/realizados`,
  title: msg`Pagamentos realizados`,
  description: msg`Os pagamentos do Pix Automático já realizados numa conta digital, agrupados por dia, com o recebedor, o status, o valor e a data, cinco por página. Clicar num abre os Detalhes do pagamento. Só consulta.`,
  access: ACCESS,
  place: { icon: 'wallet', parent: 'bankAccount.automaticPix.scheduled', record: msg`Realizados` },
  actions: [automaticPixPaidPage, automaticPixPaidCancel],
});

export const AUTOMATIC_PIX_SCREENS = [
  bankAccountAutomaticPixScreen,
  bankAccountAutomaticPixHistoryScreen,
  bankAccountAutomaticPixPendingScreen,
  bankAccountAutomaticPixActiveScreen,
  bankAccountAutomaticPixScheduledScreen,
  bankAccountAutomaticPixPaidScreen,
] as const;
